import { getLocalTimeZone, parseAbsolute } from "@internationalized/date"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Link,
  notFound,
  redirect,
} from "@tanstack/react-router"
import { AutoTextSize } from "auto-text-size"
import { GatedGetConfirmationDetailsFailReason } from "ipctg-inflow-types/InFlowEnums"
import { useTranslation } from "react-i18next"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import {
  formatZonedDateTimeWithTimeZone,
  percentFormatter,
} from "@/utilities/etc"
import { GatedGetConfirmationDetailsError } from "@/utilities/expectedErrors"
import { RATES_HREF } from "@/utilities/globals"
import {
  getConfirmationDetailsQueryOptions,
  useSendConfirmationDetails,
} from "@/utilities/hooks/queries/confirmation"
import useToggle from "@/utilities/hooks/useToggle"
import {
  normalizedAPILicensePlate,
  normalizeLongStringOfNumbers,
} from "@/utilities/normalizers"

import SendConfirmationViaMethodModal from "../../-components/SendConfirmationViaMethodModal"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/register/$sessionID/confirmation"
)({
  validateSearch: (
    search: Record<string, unknown>
  ): {
    key: string
  } => {
    return {
      key: (search.key as string) || "",
    }
  },
  beforeLoad: async ({ context, params, search }) => {
    if (!search.key) throw notFound()

    const { receiptID, showReceipt } = await context.queryClient.fetchQuery(
      getConfirmationDetailsQueryOptions({
        sessionID: params.sessionID,
        confirmationKey: search.key,
      })
    )

    if (receiptID && showReceipt) {
      throw redirect({
        to: "/receipt/$receiptID",
        params: { receiptID },
        replace: true,
      })
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { sessionID } = Route.useParams()
  const { key } = Route.useSearch()
  const { t } = useTranslation()
  const { data } = useSuspenseQuery(
    getConfirmationDetailsQueryOptions({ sessionID, confirmationKey: key })
  )
  const [isPhoneConfirmationModalOpen, togglePhoneConfirmationModalOpen] =
    useToggle(false)
  const [isEmailConfirmationModalOpen, toggleEmailConfirmationModalOpen] =
    useToggle(false)
  const { mutateAsync, reset, isPending } = useSendConfirmationDetails({
    sessionID,
    confirmationKey: key,
  })

  // Shouldn't happen because that means that it is paid and we redirect them above
  if (!data.confirmationDetails) {
    console.error(
      "No confirmation details or receipt found for this session",
      data
    )
    throw new GatedGetConfirmationDetailsError(
      GatedGetConfirmationDetailsFailReason.ConfirmationCodeNotFound
    )
  }

  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("register.confirmation.header")}
      </Heading>
      <Card>
        <Heading className="center" level={3} as={2}>
          {t("register.confirmation.subhead")}
        </Heading>
        <div className="confirmation-code">
          <AutoTextSize maxFontSizePx={48} mode="boxoneline">
            {normalizeLongStringOfNumbers(
              data.confirmationDetails.confirmationCode
            )}
          </AutoTextSize>
        </div>
        <Button
          hue="secondary"
          variant="default"
          onPress={togglePhoneConfirmationModalOpen}
        >
          <Icon name="speechBubble" slot="left" />
          {t("register.confirmation.textCode")}
        </Button>
      </Card>
      <Heading level={3} style={{ marginTop: "var(--space-8)" }}>
        {t("register.confirmation.sessionDetails.header")}
      </Heading>
      <dl>
        <div>
          <dt>
            <b>{t("register.confirmation.sessionDetails.list.licensePlate")}</b>
          </dt>
          <dd>{normalizedAPILicensePlate(data.confirmationDetails.plate)}</dd>
        </div>
        <div>
          <dt>
            <b>{t("register.confirmation.sessionDetails.list.lot")}</b>
          </dt>
          <dd>{data.confirmationDetails.lotName}</dd>
        </div>
        <div>
          <dt>
            <b>{t("register.confirmation.sessionDetails.list.entryTime")}</b>
          </dt>
          <dd>
            {formatZonedDateTimeWithTimeZone(
              parseAbsolute(
                data.confirmationDetails.entryTime,
                getLocalTimeZone()
              )
            )}
          </dd>
        </div>
        {data.confirmationDetails.paymentMethod && (
          <div>
            <dt>
              <b>
                {t("register.confirmation.sessionDetails.list.paymentMethod")}
              </b>
            </dt>
            <dd>{data.confirmationDetails.paymentMethod}</dd>
          </div>
        )}
        {data.confirmationDetails.promoCode && (
          <div style={{ color: "var(--success-10)" }}>
            <dt>{t("register.confirmation.sessionDetails.list.promoCode")}</dt>
            <dd style={{ fontWeight: 500 }}>
              {percentFormatter(data.confirmationDetails.promoDiscount)} off
            </dd>
          </div>
        )}
        <div>
          <dt>{t("register.confirmation.sessionDetails.list.totalDue")}</dt>
          <dd>
            {data.confirmationDetails.promoDiscount === 1 ? (
              "$0"
            ) : (
              <>
                {t("register.confirmation.sessionDetails.list.totaledUponExit")}{" "}
                <a href={RATES_HREF} target="_blank">
                  {t("common.seeRates")}.
                </a>
              </>
            )}
          </dd>
        </div>
      </dl>
      <Button
        hue="neutral"
        variant="light"
        style={{ width: "100%", marginTop: "var(--space-6)" }}
        onPress={toggleEmailConfirmationModalOpen}
      >
        <Icon name="mail" slot="left" />
        {t("register.confirmation.emailCode")}
      </Button>
      <Link
        className="body--2 semi-bold center"
        from={Route.fullPath}
        to="/qr/$qrLookupID/welcome"
      >
        {t("register.confirmation.registerAnother")}
      </Link>
      <SendConfirmationViaMethodModal
        method="phone"
        isOpen={isPhoneConfirmationModalOpen}
        onOpenChange={togglePhoneConfirmationModalOpen}
        defaultInput={""}
        isPending={isPending}
        mutateAsync={mutateAsync}
        resetMutation={reset}
      />
      <SendConfirmationViaMethodModal
        method="email"
        isOpen={isEmailConfirmationModalOpen}
        onOpenChange={toggleEmailConfirmationModalOpen}
        defaultInput={""}
        isPending={isPending}
        mutateAsync={mutateAsync}
        resetMutation={reset}
      />
    </>
  )
}
